import { Box } from '@mui/material';

import Description from '../../components/Description/Description';
import LivingConditionField from '../../components/LivingConditionField/LivingConditionField';
import NumericField from '../../components/NumericField/NumericField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import {
    APPLICANT_AGE_CHILD_FIELD_PREFIX,
    CHILD_COUNT_SELECTOR_FIELD_NAME,
    MAX_CHILD_AGE,
    MAX_CHILDREN,
    MIN_CHILD_AGE,
} from '../../store/applicationform/childAgeGroupUtil';
import { WizardPageAction } from '../../store/wizard/wizard';

const LivingConditionPage = () => {
    useComponentTracker('LivingConditionPage');

    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();

    const rawChildCount = Number(useAppSelector(selectFieldValue)(CHILD_COUNT_SELECTOR_FIELD_NAME)) || 0;
    const childCount = Math.min(Math.max(rawChildCount, 0), MAX_CHILDREN);
    const applicantAgeChildFieldNames = Array.from(
        { length: childCount },
        (_, index) => `${APPLICANT_AGE_CHILD_FIELD_PREFIX}${index + 1}`,
    );

    const validationErrors = useValidation([
        'applicantMaritalStatus',
        CHILD_COUNT_SELECTOR_FIELD_NAME,
        ...applicantAgeChildFieldNames,
    ]);

    // Avoid NumericField's min/max clamping here: it turns a cleared input into '0'
    // (Number('') is 0, not NaN), which would let a required age field be silently
    // "answered" as age 0 instead of failing the required-field check on next.
    const childAgeValidation = async (value?: string): Promise<ValidationResultType> => {
        const age = Number(value);
        const isValid = Number.isInteger(age) && age >= MIN_CHILD_AGE && age <= MAX_CHILD_AGE;

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : 'validation.required',
        };
    };

    useSetBannerType('bannerOne');

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.living-condition.title')} />
            <Description text={translate('page.living-condition.description')} />
            <ShowDetailsModal
                label={translate('dialog.know-more.label')}
                dialogTitle={translate('dialog.know-more.label')}
                dialogContent={translate('dialog.living-condition.content')}
            />
            <LivingConditionField name="applicantMaritalStatus" />
            {/*
                NumericField carries margin on both sides (my: 2), and MUI's FormControl root is
                inline-flex, so those margins never collapse the way block-level margins normally
                would - stacking two of them back to back doubles the gap to 32px instead of the
                16px used everywhere else on this page. Zero out the top margin of every field that
                follows another one here to restore that single 16px rhythm.
            */}
            <Box sx={{ '& > .MuiFormControl-root + .MuiFormControl-root': { marginTop: 0 } }}>
                <NumericField
                    name={CHILD_COUNT_SELECTOR_FIELD_NAME}
                    label={translate('page.living-condition.subtitle.children')}
                    min={0}
                    max={MAX_CHILDREN}
                />
                {applicantAgeChildFieldNames.map((fieldName, index) => (
                    <NumericField
                        key={fieldName}
                        name={fieldName}
                        label={translate('page.living-condition.subtitle.child-age', { index: index + 1 })}
                        customValidation={childAgeValidation}
                    />
                ))}
            </Box>
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default LivingConditionPage;
